import { useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api, jsonBody } from './api'
import type { SlideTransition } from './content-pages'
import {
  OverlayCardPreview,
  type Overlay,
  type OverlayCard,
  type OverlayCardType,
  type OverlayPosition,
} from './overlay-preview'
import { SignageFontSelect } from './signage-fonts'
import { EmptyState, PageHeader } from './ui'

type OverlayDraft = Omit<Overlay, 'id' | 'createdAt' | 'updatedAt'>

const newCard = (): OverlayCard => ({
  id: crypto.randomUUID(),
  type: 'TEXT',
  line1: 'DOT IT',
  line2: 'Digital Signage',
  icon: '●',
  durationMs: 5000,
  timezone: 'Asia/Dhaka',
  locale: 'en-GB',
  showSeconds: true,
  hourCycle: 'H12',
})
const newDraft = (): OverlayDraft => ({
  name: '',
  position: 'TICKER_SIDE',
  cards: [newCard()],
  defaultDurationMs: 5000,
  transition: 'FADE',
  backgroundColor: '#991b1b',
  textColor: '#ffffff',
  fontFamily: 'Hind Siliguri',
  fontSize: 24,
  fontWeight: 700,
  borderRadius: 12,
})

export function OverlaysPage() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<OverlayDraft>(() => newDraft())
  const overlays = useQuery({
    queryKey: ['overlays'],
    queryFn: () => api<Overlay[]>('/overlays'),
    refetchOnMount: 'always',
  })
  const editing = overlays.data?.find((item) => item.id === editingId)
  const preview = useMemo<Overlay>(
    () => ({
      id: editingId ?? 'preview-overlay',
      ...draft,
      createdAt: '',
      updatedAt: '',
    }),
    [draft, editingId]
  )
  const save = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: unknown }) =>
      api(id ? `/overlays/${id}` : '/overlays', {
        method: id ? 'PUT' : 'POST',
        body: jsonBody(body),
      }),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['overlays'] }),
        queryClient.invalidateQueries({ queryKey: ['playlists'] }),
      ])
      setOpen(false)
      setEditingId(null)
      toast.success(variables.id ? 'Overlay updated.' : 'Overlay created.')
    },
    onError: (error) => toast.error(error.message),
  })
  const startNew = () => {
    setEditingId(null)
    setDraft(newDraft())
    setOpen(true)
  }
  const startEditing = (overlay: Overlay) => {
    setEditingId(overlay.id)
    setDraft({
      name: overlay.name,
      position: overlay.position,
      cards: overlay.cards.map((card) => ({
        ...card,
        type: card.type ?? 'TEXT',
        timezone: card.timezone ?? 'Asia/Dhaka',
        locale: card.locale ?? 'en-GB',
        showSeconds: card.showSeconds !== false,
        hourCycle: card.hourCycle ?? 'H12',
      })),
      defaultDurationMs: overlay.defaultDurationMs,
      transition: overlay.transition,
      backgroundColor: overlay.backgroundColor,
      textColor: overlay.textColor,
      fontFamily: overlay.fontFamily,
      fontSize: overlay.fontSize,
      fontWeight: overlay.fontWeight,
      borderRadius: overlay.borderRadius,
    })
    setOpen(true)
  }
  const updateCard = (id: string, patch: Partial<OverlayCard>) =>
    setDraft((current) => ({
      ...current,
      cards: current.cards.map((card) =>
        card.id === id ? { ...card, ...patch } : card
      ),
    }))
  const submit = (event: FormEvent) => {
    event.preventDefault()
    save.mutate({ id: editing?.id, body: draft })
  }

  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Content'
        title='Overlays'
        description='Create reusable one or two-line rotating cards. Use one as the ticker’s left badge or place it in a screen corner, then assign it to any playlist.'
        action={
          <Button onClick={startNew}>
            <Plus className='size-4' />
            New overlay
          </Button>
        }
      />
      <Dialog
        open={open}
        onOpenChange={(value) => {
          setOpen(value)
          if (!value) setEditingId(null)
        }}
      >
        <DialogContent className='max-h-[94vh] overflow-y-auto sm:max-w-5xl'>
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Edit overlay' : 'Create overlay'}
            </DialogTitle>
            <DialogDescription>
              Each card can have one or two lines. Multiple cards rotate
              automatically with the selected transition.
            </DialogDescription>
          </DialogHeader>
          <form className='space-y-5' onSubmit={submit}>
            <div className='grid aspect-video place-items-center overflow-hidden rounded-xl bg-zinc-950 p-6'>
              <OverlayCardPreview overlay={preview} />
            </div>
            <div className='grid gap-4 sm:grid-cols-3'>
              <div className='space-y-2'>
                <Label>Name</Label>
                <Input
                  value={draft.name}
                  required
                  placeholder='Brand badge'
                  onChange={(e) =>
                    setDraft((v) => ({ ...v, name: e.target.value }))
                  }
                />
              </div>
              <div className='space-y-2'>
                <Label>Placement</Label>
                <select
                  className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                  value={draft.position}
                  onChange={(e) =>
                    setDraft((v) => ({
                      ...v,
                      position: e.target.value as OverlayPosition,
                    }))
                  }
                >
                  <option value='TICKER_SIDE'>Ticker side panel</option>
                  <option value='TICKER_LEFT'>Legacy ticker side</option>
                  <option value='TOP_LEFT'>Top left</option>
                  <option value='TOP_RIGHT'>Top right</option>
                  <option value='BOTTOM_LEFT'>Bottom left</option>
                  <option value='BOTTOM_RIGHT'>Bottom right</option>
                </select>
              </div>
              <div className='space-y-2'>
                <Label>Font</Label>
                <SignageFontSelect
                  value={draft.fontFamily}
                  onChange={(e) =>
                    setDraft((v) => ({ ...v, fontFamily: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className='grid gap-4 sm:grid-cols-4'>
              <div className='space-y-2'>
                <Label>Transition</Label>
                <select
                  className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                  value={draft.transition}
                  onChange={(e) =>
                    setDraft((v) => ({
                      ...v,
                      transition: e.target.value as SlideTransition,
                    }))
                  }
                >
                  <option value='NONE'>Cut</option>
                  <option value='FADE'>Fade</option>
                  <option value='SLIDE_LEFT'>Slide left</option>
                  <option value='SLIDE_RIGHT'>Slide right</option>
                  <option value='ZOOM'>Zoom</option>
                  <option value='WIPE'>Wipe</option>
                </select>
              </div>
              <div className='space-y-2'>
                <Label>Default seconds</Label>
                <Input
                  type='number'
                  min={1}
                  max={60}
                  value={draft.defaultDurationMs / 1000}
                  onChange={(e) =>
                    setDraft((v) => ({
                      ...v,
                      defaultDurationMs: Number(e.target.value) * 1000,
                    }))
                  }
                />
              </div>
              <div className='space-y-2'>
                <Label>Font size</Label>
                <Input
                  type='number'
                  min={8}
                  max={160}
                  value={draft.fontSize}
                  onChange={(e) =>
                    setDraft((v) => ({
                      ...v,
                      fontSize: Number(e.target.value),
                    }))
                  }
                />
              </div>
              <div className='space-y-2'>
                <Label>Weight</Label>
                <select
                  className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                  value={draft.fontWeight}
                  onChange={(e) =>
                    setDraft((v) => ({
                      ...v,
                      fontWeight: Number(e.target.value),
                    }))
                  }
                >
                  <option value={400}>Regular</option>
                  <option value={500}>Medium</option>
                  <option value={600}>Semi bold</option>
                  <option value={700}>Bold</option>
                  <option value={800}>Extra bold</option>
                </select>
              </div>
              <div className='space-y-2'>
                <Label>Background</Label>
                <Input
                  type='color'
                  value={draft.backgroundColor}
                  onChange={(e) =>
                    setDraft((v) => ({ ...v, backgroundColor: e.target.value }))
                  }
                />
              </div>
              <div className='space-y-2'>
                <Label>Text</Label>
                <Input
                  type='color'
                  value={draft.textColor}
                  onChange={(e) =>
                    setDraft((v) => ({ ...v, textColor: e.target.value }))
                  }
                />
              </div>
              <div className='space-y-2'>
                <Label>Corner radius</Label>
                <Input
                  type='number'
                  min={0}
                  max={64}
                  value={draft.borderRadius}
                  onChange={(e) =>
                    setDraft((v) => ({
                      ...v,
                      borderRadius: Number(e.target.value),
                    }))
                  }
                />
              </div>
            </div>
            <div className='space-y-3'>
              <div className='flex items-center justify-between'>
                <div>
                  <h3 className='font-semibold'>Rotating cards</h3>
                  <p className='text-sm text-muted-foreground'>
                    Keep line two empty for a single-line card.
                  </p>
                </div>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() =>
                    setDraft((v) => ({ ...v, cards: [...v.cards, newCard()] }))
                  }
                >
                  <Plus className='size-4' />
                  Add card
                </Button>
              </div>
              {draft.cards.map((card, index) => (
                <section
                  key={card.id}
                  className='space-y-4 rounded-xl border p-4'
                >
                  <div className='grid gap-3 sm:grid-cols-[170px_90px_120px_44px]'>
                    <div className='space-y-2'>
                      <Label>Card type</Label>
                      <select
                        className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                        value={card.type ?? 'TEXT'}
                        onChange={(e) =>
                          updateCard(card.id, {
                            type: e.target.value as OverlayCardType,
                          })
                        }
                      >
                        <option value='TEXT'>Text</option>
                        <option value='CLOCK'>Live clock</option>
                        <option value='DATE'>Live date</option>
                      </select>
                    </div>
                    <div className='space-y-2'>
                      <Label>Icon</Label>
                      <Input
                        value={card.icon ?? ''}
                        maxLength={8}
                        placeholder='●'
                        onChange={(e) =>
                          updateCard(card.id, { icon: e.target.value || null })
                        }
                      />
                    </div>
                    <div className='space-y-2'>
                      <Label>Seconds</Label>
                      <Input
                        type='number'
                        min={1}
                        max={60}
                        value={
                          (card.durationMs ?? draft.defaultDurationMs) / 1000
                        }
                        onChange={(e) =>
                          updateCard(card.id, {
                            durationMs: Number(e.target.value) * 1000,
                          })
                        }
                      />
                    </div>
                    <div className='flex items-end'>
                      <Button
                        type='button'
                        size='icon'
                        variant='ghost'
                        disabled={draft.cards.length === 1}
                        aria-label={`Remove card ${index + 1}`}
                        onClick={() =>
                          setDraft((v) => ({
                            ...v,
                            cards: v.cards.filter(
                              (item) => item.id !== card.id
                            ),
                          }))
                        }
                      >
                        <Trash2 className='size-4' />
                      </Button>
                    </div>
                  </div>
                  {(card.type ?? 'TEXT') === 'TEXT' ? (
                    <div className='grid gap-3 sm:grid-cols-2'>
                      <div className='space-y-2'>
                        <Label>Line 1</Label>
                        <Input
                          value={card.line1}
                          required
                          maxLength={120}
                          placeholder={index ? 'Special offer' : 'DOT IT'}
                          onChange={(e) =>
                            updateCard(card.id, { line1: e.target.value })
                          }
                        />
                      </div>
                      <div className='space-y-2'>
                        <Label>Line 2 (optional)</Label>
                        <Input
                          value={card.line2 ?? ''}
                          maxLength={160}
                          placeholder='Digital Signage'
                          onChange={(e) =>
                            updateCard(card.id, {
                              line2: e.target.value || null,
                            })
                          }
                        />
                      </div>
                    </div>
                  ) : (
                    <div className='grid gap-3 sm:grid-cols-4'>
                      <div className='space-y-2'>
                        <Label>Optional label</Label>
                        <Input
                          value={card.line1}
                          maxLength={120}
                          placeholder='Dhaka'
                          onChange={(e) =>
                            updateCard(card.id, { line1: e.target.value })
                          }
                        />
                      </div>
                      <div className='space-y-2'>
                        <Label>Timezone</Label>
                        <Input
                          value={card.timezone ?? 'Asia/Dhaka'}
                          placeholder='Asia/Dhaka'
                          onChange={(e) =>
                            updateCard(card.id, { timezone: e.target.value })
                          }
                        />
                      </div>
                      <div className='space-y-2'>
                        <Label>Language</Label>
                        <select
                          className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                          value={card.locale ?? 'en-GB'}
                          onChange={(e) =>
                            updateCard(card.id, { locale: e.target.value })
                          }
                        >
                          <option value='en-GB'>English</option>
                          <option value='bn-BD'>বাংলা</option>
                          <option value='ar-SA'>العربية</option>
                        </select>
                      </div>
                      {(card.type ?? 'TEXT') === 'CLOCK' ? (
                        <div className='space-y-2'>
                          <Label>Clock format</Label>
                          <select
                            className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                            value={card.hourCycle ?? 'H12'}
                            onChange={(e) =>
                              updateCard(card.id, {
                                hourCycle: e.target.value as 'H12' | 'H24',
                              })
                            }
                          >
                            <option value='H12'>12-hour</option>
                            <option value='H24'>24-hour</option>
                          </select>
                          <label className='flex items-center gap-2 text-xs'>
                            <input
                              type='checkbox'
                              checked={card.showSeconds !== false}
                              onChange={(e) =>
                                updateCard(card.id, {
                                  showSeconds: e.target.checked,
                                })
                              }
                            />
                            Show seconds
                          </label>
                        </div>
                      ) : null}
                    </div>
                  )}
                </section>
              ))}
            </div>
            <Button
              className='w-full'
              disabled={
                save.isPending ||
                !draft.cards.every(
                  (card) =>
                    (card.type ?? 'TEXT') !== 'TEXT' || card.line1.trim()
                )
              }
            >
              {save.isPending
                ? 'Saving…'
                : editing
                  ? 'Save overlay changes'
                  : 'Create overlay'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      {overlays.isError ? (
        <EmptyState
          title='Could not load overlays'
          description={overlays.error.message}
        />
      ) : overlays.data?.length ? (
        <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
          {overlays.data.map((overlay) => (
            <article
              key={overlay.id}
              className='space-y-4 rounded-xl border bg-card p-4'
            >
              <div className='flex min-h-24 items-center justify-center overflow-hidden rounded-lg bg-zinc-950 p-3'>
                <OverlayCardPreview overlay={overlay} />
              </div>
              <div className='flex items-start justify-between gap-3'>
                <div>
                  <h3 className='font-semibold'>{overlay.name}</h3>
                  <p className='text-sm text-muted-foreground'>
                    {overlay.cards.length} card(s) ·{' '}
                    {overlay.position.toLowerCase().replaceAll('_', ' ')}
                  </p>
                </div>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => startEditing(overlay)}
                >
                  <Pencil className='size-4' />
                  Edit
                </Button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <EmptyState
          title='No overlays yet'
          description='Create your editable DOT IT badge, offer cards, labels, or corner overlays.'
          action={<Button onClick={startNew}>Create overlay</Button>}
        />
      )}
    </div>
  )
}
